import React, { useState } from 'react';
import ReactGA from 'react-ga4';
import logo from './assets/buyerry.svg';
import './App.css';
import { SignIn } from './components/auth/SignIn'
import { AuthDetails } from './components/auth/AuthDetails'
import { useSelector, useDispatch } from 'react-redux';
import { selectAnonSearches, selectUser} from './redux/identitySlice';
import { Box, Backdrop, CircularProgress} from '@mui/material/';
import { setInside, selectChatSessionId, selectIsInside, addToDiscourse, clearData, setChatSessionId, setSearching, setProducts, markAutoSearched, setHistory } from './redux/dataSlice';
import { MainPage } from './pages/MainPage';
import { TextFront } from './components/TextFront';
import { SignInDialog } from './components/SignInDialog';
import { signOut } from "firebase/auth";
import { auth } from "./firebase";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import useMediaQuery from './pages/useMediaQuery';
import gaEvent from "./components/handleEvent"
const {RestFunctions} = require('./repositories/Network')

const theme = createTheme({
  typography: {
    fontFamily: [
        ['SF Pro Display', 'sans-serif'],
        'Roboto',
        'Raleway',
        'Open Sans',
    ].join(','),
    h1: {
        fontSize: '5rem',
        fontFamily: 'Raleway',
    },
    h2: {
        fontSize: '3.5rem',
        fontFamily: 'Open Sans',
        fontStyle: 'bold',
    },
    h3: {
        fontSize: '2.5rem',
        fontFamily: 'Roboto',
    },
},
});

function App() {
  ReactGA.initialize('G-42HB4PSV1E');

  const user = useSelector(selectUser)
  const isInside = useSelector(selectIsInside)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const chatSessionId = useSelector(selectChatSessionId)
  const anonSearches = useSelector(selectAnonSearches)
  const [querying, setQuerying] = useState(false);
  const dispatch = useDispatch()
  const [isTyping, setIsTyping] = useState(false);

  const converse = async (message) => {
    if (checkSignupRequired()) return
    if (message === '') return

    setQuerying(true);
    setIsTyping(true);
    dispatch(addToDiscourse({ actor: 'user', message: message}))

    try {
        const response = await RestFunctions.query(message, chatSessionId); // Pass setIsTyping to query
        if (response) {
            if (response.request_id) {
                dispatch(setChatSessionId(response.request_id));
            }
            // Tag the AI message with messageId when the server provides it,
            // so ChatDialog can correlate it with autoSearchedMessageIds.
            dispatch(addToDiscourse({
                actor: 'ai',
                message: response.ai_response,
                messageId: response.message_id ?? null,
            }));
            dispatch(setInside(true));

            // Auto-search path: server fired the search in a background
            // thread and returned immediately. We show the message right
            // away and poll until the results are ready.
            if (response.action === 'search' && response.message_id != null) {
                dispatch(markAutoSearched(response.message_id));
                dispatch(setSearching(true));
                gaEvent('User', 'Auto Search', 'Server-side Auto Search');

                // Refresh the left-menu history so the new search appears immediately
                const refreshHistory = () => {
                    RestFunctions.gcpData({ target: "user_data" }).then((e) => {
                        if (e?.data) dispatch(setHistory(e.data));
                    }).catch(() => {});
                };
                refreshHistory();

                // Poll every 2s for up to 90s.
                // Render products immediately as they arrive (RUNNING or COMPLETED).
                // Stop polling only when COMPLETED or timed out.
                const pollStart = Date.now();
                let lastProductCount = 0;
                const poll = async () => {
                    try {
                        const productsResponse = await RestFunctions.getProducts(response.message_id);
                        const status = productsResponse?.status;
                        const products = productsResponse?.products;

                        // Render any new products that have arrived since last poll
                        if (products && products.length > lastProductCount) {
                            lastProductCount = products.length;
                            const rezults = products.map((e) => ({
                                images: e.medias,
                                product_data: e.product_data,
                                reviews: e.reviews,
                                score: e.score,
                            }));
                            dispatch(setProducts(rezults));
                        }

                        if (status === 'COMPLETED') {
                            dispatch(setSearching(false));
                            // Refresh history so search_summary/search_id are up to date
                            refreshHistory();
                        } else if (Date.now() - pollStart < 90000) {
                            setTimeout(poll, 2000);
                        } else {
                            dispatch(setSearching(false));
                        }
                    } catch (err) {
                        console.log('Auto-search poll failed:', err);
                        if (Date.now() - pollStart < 90000) {
                            setTimeout(poll, 2000);
                        } else {
                            dispatch(setSearching(false));
                        }
                    }
                };
                setTimeout(poll, 2000);
            }
        }
    } finally {
        setIsTyping(false);
        setQuerying(false);
    }
  }

  const checkSignupRequired = () => {
    if (!user && anonSearches?.length > 1) {
      setOpenDialog(true);
      gaEvent('User', 'Signup Requested', 'Signup Requested');
      return true
    }
    return false
  }

  const userSignOut = () => {
    signOut(auth)
      .then(() => {
        console.log("AuthDetails: sign out successful");
        gaEvent('User', 'Sign Out', 'User Signed Out');
        dispatch(clearData(true))
      })
      .catch((error) => console.log(error));
  };

  const [openDialog, setOpenDialog] = React.useState(false);
  const handleClickOpenDialog = () => {
      setOpenDialog(true);
  }
  const handleCloseDialog = () => {
      setOpenDialog(false);
  }

  if (navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i)) {
    var viewportmeta = document.querySelectorAll('meta[name="viewport"]')[0];
    if (viewportmeta) {
        viewportmeta.content = 'width=device-width, minimum-scale=1.0, maximum-scale=1.0';
        document.body.addEventListener('gesturestart', function() {
            viewportmeta.content = 'width=device-width, minimum-scale=0.25, maximum-scale=1.6';
        }, false);
    }
}

  return (

      <div className={`App ${isInside ? '' : 'rotating-background'}`} style={{
          height: '100vh',
          width: '100vw',
          color: 'black',
          fontSize: 16,
          fontFamily: 'SF Pro Display',
          background: 'radial-gradient(ellipse at 90% 10%, rgba(102, 181, 219, 0.15) 20%, transparent 40%), radial-gradient(ellipse at 10% 80%, rgba(205, 145, 0, 0.1) 20%, transparent 40%)',
          backgroundColor: `#ffffffb7`,
      }}>
          <ThemeProvider theme={theme}>
              {!user &&
                  <SignInDialog
                      message='You have been granted 2 anonymous searches. If you wish to continue your journey, signup with our service for free'
                      openDialog={openDialog}
                      handleCloseDialog={handleCloseDialog}
                      className="sign-in-bg"
                  />
              }

              <AuthDetails show={false}/>
              {user?.accessToken || isInside ?
                  <>
                      <MainPage
                          chatSessionId={chatSessionId}
                          conversationHandler={converse}
                          checkSignupRequired={checkSignupRequired}
                          userSignOut={userSignOut}
                          isMobile={isMobile}
                          isTyping={isTyping}
                          setIsTyping={setIsTyping}
                      />
                  </>
                  :
                  <>
                      <Backdrop
                          sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000000}}
                          open={querying}
                          className='main-backdrop'>
                          <CircularProgress color="inherit"/>
                      </Backdrop>
                      <Box
                          sx={{
                              height: '100vh',
                              width: '100vw',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'top',
                              textAlign: 'center',
                              overflow: 'hidden',
                          }}>

                          <Box
                              sx={{
                                  maxWidth: '100%',
                                  width: '100%',
                                  padding: '30px',
                                  alignContent: 'center',
                              }}
                          >
                              <header className="App-header">
                                  <img src={logo} className="App-logo" alt="logo"/>
                              </header>
                              <Box sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                              }}>
                                  <Box sx={{
                                      width: '100%',
                                      mt: 4,
                                  }}>
                                      <TextFront converseHandler={converse}/>
                                  </Box>
                              </Box>
                              <SignIn/>
                          </Box>
                      </Box>
                      <div className='footers'>
                          <span><a href='https://buyerry.com/about/'>About</a></span>
                          <span><a href='https://buyerry.com/investors/'>Investment</a></span>
                          <span><a href='https://buyerry.com/privacy-policy/'>Privacy Policy</a></span>
                          <span><a href='https://buyerry.com/terms-and-conditions/'>Terms and Conditions</a></span>
                      </div>
                  </>
              }
          </ThemeProvider>


      </div>
  );
}

export default App;
